import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getTileUrl, getModelCoordinates } from '../config';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';

export class AlpenglowLayer extends BaseProduct {
  public readonly id: ProductId = 'alpenglow';
  private unsubscribeStore: (() => void) | null = null;
  private lastMode: string = '';
  private lastModel: string = '';

  protected onActivate(): void {
    this.refresh();
    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      const currentMode = s.alpenglowMode || 'goldenhour';
      const isGoldenHour = currentMode === 'goldenhour';
      const targetProduct: ProductId = isGoldenHour ? 'goldenhour' : 'alpenglow';
      const currentModel = s.selectedModels[targetProduct] || s.selectedModels['alpenglow'] || 'gw_v2_blend_50';
      if (currentMode !== this.lastMode || currentModel !== this.lastModel) {
        this.lastMode = currentMode;
        this.lastModel = currentModel;
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.lastMode = '';
    this.lastModel = '';
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(targetTimeMs?: number) {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const isGoldenHour = state.alpenglowMode === 'goldenhour';
    const targetProduct: ProductId = isGoldenHour ? 'goldenhour' : 'alpenglow';
    const defaultModel = 'gw_v2_blend_50';
    const model = state.selectedModels[targetProduct] || defaultModel;
    const opacity = getLayerOpacity(this.id, 1.0);

    const rawTime = targetTimeMs !== undefined ? new Date(targetTimeMs) : state.currentTimeStep;
    if (!rawTime) return;

    let dateStr: string;
    let event: string;

    if (state.webcamsEnabled && state.selectedWebcamDate) {
      dateStr = state.selectedWebcamDate;
      const tz = state.userTimezone || 'America/Los_Angeles';
      let isMorning = true;
      try {
        const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false });
        const localHour = parseInt(dFmt.format(rawTime), 10);
        isMorning = (localHour === 24 ? 0 : localHour) < 12;
      } catch {
        isMorning = rawTime.getUTCHours() < 12;
      }
      event = isMorning 
        ? (isGoldenHour ? 'goldenhour_morning' : 'alpenglow_morning') 
        : (isGoldenHour ? 'goldenhour_evening' : 'alpenglow_evening');
    } else {
      const clampedFrame = ManifestTimelineHelper.getClampedFrame(manifest, targetProduct, model, rawTime) 
        || ManifestTimelineHelper.getClampedFrame(manifest, targetProduct, defaultModel, rawTime)
        || ManifestTimelineHelper.getClampedFrame(manifest, targetProduct, 'gw_v2_blend_50', rawTime);
      dateStr = clampedFrame ? clampedFrame.dateStr : rawTime.toISOString().split('T')[0];
      
      let baseEvent = clampedFrame ? clampedFrame.hourStr : 'alpenglow_morning';
      if (isGoldenHour) {
        event = (baseEvent.includes('evening') || baseEvent === 'sunset') ? 'goldenhour_evening' : 'goldenhour_morning';
      } else {
        event = (baseEvent.includes('evening') || baseEvent === 'sunset') ? 'alpenglow_evening' : 'alpenglow_morning';
      }
    }

    const tiles = [
      getTileUrl(manifest, targetProduct, model, dateStr, event)
    ];

    this.layerManager.updateBufferedRasterLayer(this.id, tiles, opacity, undefined, 0, 5, 'linear', getModelCoordinates(manifest, targetProduct, model));
  }
}
export default AlpenglowLayer;
